import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatEuro } from '../lib/format';
import { SummaryCard } from '../components/tresorerie/SummaryCard';

export function TresoreriePage(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-tresorerie'],
    queryFn: () => api.dashboard.tresorerie(),
  });

  const configured = data?.configured ?? false;

  return (
    <div className="max-w-7xl space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Trésorerie</h1>
          <p className="text-xs text-slate-500 mt-1">
            Encaissements, dette Global Wood, charges, cash flow prévisionnel.
          </p>
        </div>
      </header>

      {!configured && !isLoading && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 text-sm text-amber-200">
          La dette Global Wood n'est pas encore initialisée. Initialisez-la depuis la page dédiée
          pour activer les cards et le cash flow.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard
          label="Dette Global Wood"
          value={
            data?.dette_global_wood_restant_centimes === null ||
            data?.dette_global_wood_restant_centimes === undefined
              ? 'Non configuré'
              : formatEuro(data.dette_global_wood_restant_centimes)
          }
          hint="Restant à rembourser"
        />
        <SummaryCard
          label="Total impayés"
          value={
            data?.total_impayes_centimes === null || data?.total_impayes_centimes === undefined
              ? '—'
              : formatEuro(data.total_impayes_centimes)
          }
          tone="warning"
        />
        <SummaryCard
          label="Impayés en retard"
          value={
            data?.total_impayes_en_retard_centimes === null ||
            data?.total_impayes_en_retard_centimes === undefined
              ? '—'
              : formatEuro(data.total_impayes_en_retard_centimes)
          }
          tone={(data?.total_impayes_en_retard_centimes ?? 0) > 0 ? 'negative' : 'neutral'}
        />
        <SummaryCard
          label="Solde prévisionnel 3 mois"
          value={
            data?.solde_previsionnel_3m_centimes === null ||
            data?.solde_previsionnel_3m_centimes === undefined
              ? '—'
              : formatEuro(data.solde_previsionnel_3m_centimes)
          }
          tone={(data?.solde_previsionnel_3m_centimes ?? 0) >= 0 ? 'positive' : 'negative'}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <NavCard
          to="/tresorerie/cashflow"
          icon="📊"
          title="Cash flow prévisionnel"
          desc="Entrées et sorties projetées sur 3 à 24 mois."
        />
        <NavCard
          to="/tresorerie/encaissements"
          icon="💳"
          title="Encaissements / Impayés"
          desc="Ventes non soldées, statut effectif, paiements."
        />
        <NavCard
          to="/tresorerie/acomptes"
          icon="💰"
          title="Acomptes"
          desc="Acomptes reçus avant facturation finale."
        />
        <NavCard
          to="/tresorerie/avoirs"
          icon="↩"
          title="Avoirs"
          desc="Avoirs Crédit ou Remboursement par vente."
        />
        <NavCard
          to="/tresorerie/charges-fixes"
          icon="🏠"
          title="Charges fixes"
          desc="Loyer, salaires, cotisations, abonnements…"
        />
        <NavCard
          to="/tresorerie/global-wood"
          icon="🏗"
          title="Dette Global Wood"
          desc="Capital initial, paiements, date de soldage."
        />
      </div>
    </div>
  );
}

function NavCard({
  to,
  icon,
  title,
  desc,
}: {
  to: string;
  icon: string;
  title: string;
  desc: string;
}): JSX.Element {
  return (
    <Link
      to={to}
      className="block bg-slate-900 border border-slate-800 rounded-lg p-4 hover:border-floryon-green/50 transition-colors"
    >
      <div className="text-2xl">{icon}</div>
      <div className="mt-2 font-medium text-slate-100">{title}</div>
      <div className="text-xs text-slate-500 mt-1">{desc}</div>
    </Link>
  );
}
